import { UserDb } from 'lib/cosmos/user/userDb.js';
import { isNotUndefined } from 'lib/helpers/array.utils.js';
import { map, of } from 'rxjs';
import { CoreAppComponentDependencies } from 'src/app/core/core-app-dependencies.js';
import { routeParams } from 'src/app/routeParams.js';
import { ChatConversationComponent } from '../../modules/chat/chat-conversation.component.js';
import { ChatComponent } from '../../modules/chat/chat.component.js';
import {
	ICoreRoutes,
	IPageMetadata,
	IPageTag,
	ISharedData,
	ITypePageCard,
	ITypeRoute,
	ITypeRouteAction,
	RouteInitializer
} from '../../pages/type-routes/type-route.models.js';

export interface IChatRoutes extends ICoreRoutes {
	overview: ITypeRoute<
		undefined,
		{
			list: ITypeRouteAction<undefined>;
		}
	>;
	conversation: ITypeRoute<
		UserDb.IUserItem,
		{
			view: ITypeRouteAction<UserDb.IUserItem>;
		}
	>;
}

export const chatRoutes: RouteInitializer<IChatRoutes> = {
	overview: {
		mainPath: 'chat',
		uniqueId: 'chatOverview',
		parentRoutes: (context) => [],
		loadSharedData: (context) => {
			return of(undefined);
		},
		menuItems: (context) => [],
		children: {
			list: {
				uniqueId: `chatList`,
				component: ChatComponent,
				getMetadata: (data) => {
					const metadata: IPageMetadata = {
						title: 'Conversions'
					};
					return metadata;
				}
			}
		}
	},
	conversation: {
		mainPath: 'chat',
		uniqueId: 'conversation',
		parentRoutes: (context) => {
			if (context.user?.userType === 'trainer' && context.sharedData) {
				return [
					{
						title: context.sharedData.data.fullName,
						routerLink: context.dependencies.navigateService
							.actionRoute(
								(routes) => routes.chatRoutes.conversation.children.view,
								context.routeData.primaryId
							)
							.getUrl()
					}
				];
			}

			return [];
		},
		loadSharedData: (context) => {
			return context.dependencies.httpService
				.get<UserDb.IUserItem>((call) =>
					call
						.withCustomApiRoute('user', 'getUser')
						.withId(
							context.authUser.userType === 'trainer' ? context.routeData.primaryId : context.authUser.id
						)
						.withQueryString(routeParams.loadTrainer, true)
						.build()
				)
				.pipe(
					map((response) => {
						const sharedData: ISharedData<UserDb.IUserItem> = {
							data: response.data,
							linkedItems: response.linkedItems
						};

						return sharedData;
					})
				);
		},
		menuItems: (context) => [],
		children: {
			view: {
				uniqueId: `chatConversation`,
				component: ChatConversationComponent,
				getMetadata: (data) => {
					const authUser = data.dependencies.authService.authUser();

					if (!authUser) {
						return { title: 'Invalid chat auth user' };
					}

					if (authUser.userType === 'client') {
						const trainer = data.sharedData?.linkedItems
							?.filter(UserDb.isUserItem)
							.find((item) => item.id === authUser.trainerId);

						if (!trainer) {
							return { title: 'Invalid chat trainer' };
						}

						return {
							title: 'Conversation',
							card: getUserCard(trainer, data.dependencies)
						};
					}

					const client = data.sharedData?.data;

					if (!client) {
						return { title: 'Invalid chat client' };
					}

					return {
						title: 'Conversation',
						card: getUserCard(client, data.dependencies)
					};
				}
			}
		}
	}
};

function getUserCard(user: UserDb.IUserItem, dependencies: CoreAppComponentDependencies): ITypePageCard | undefined {
	if (!user) {
		return undefined;
	}

	const timezoneTag: IPageTag | undefined = user.timezone
		? {
				icon: 'timezone',
				text: user.timezone
		  }
		: undefined;

	const tags: readonly (IPageTag | undefined)[] = [
		{
			icon: 'email',
			text: user.email
		},
		timezoneTag
	];

	return {
		title: { value: user.fullName },
		subtitle: user.lastActiveDate
			? `Last active ${dependencies.timeService.fromNow(user.lastActiveDate)}`
			: undefined,
		avatar: {
			avatar: user.avatar,
			avatarName: user.fullName
		},
		tags: tags.filter(isNotUndefined)
	};
}
